// utils
import { DealExchangeValidator } from '../vehicleDealExchangeValidator/vehicleDealExchangeValidator';

// types
import { vehicleUtils } from '@makemydeal/dr-platform-shared';
import type { CommonOrgDealerVehicle } from '@makemydeal/dr-shared-types/vehicleTypes';

type ConditionEnum = 'new' | 'used' | 'demo' | 'certified' | 'rental' | 'consignment' | 'other';

const AcceptedEnums = {
    condition: {
        map: {
            new: 'new',
            used: 'used',
            demo: 'demo',
            certified: 'certified',
            certifiedpreowned: 'certified',
            rental: 'rental',
            consignment: 'consignment',
            other: 'other'
        },
        default: 'used'
    }
} as const;

const conditionMapper = (value?: string): ConditionEnum => {
    if (!value) return AcceptedEnums.condition.default;

    return (
        AcceptedEnums.condition.map[value.toLowerCase() as keyof typeof AcceptedEnums.condition.map] ||
        AcceptedEnums.condition.default
    );
};

const extractGvwr = (value: unknown): number | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && 'value' in value) return (value as { value?: number }).value;
    return undefined;
};

export const cviToVehicle = (data: CommonOrgDealerVehicle) => {
    const condition = conditionMapper(data.condition);

    return {
        bodyStyle: data.bodyStyle,
        cityFuelEconomy: data.cityFuelEconomy,
        condition,
        cost: data.cost || data.wholesalePrice,
        destinationCharge: data.destinationCharge ?? undefined,
        driveLine: DealExchangeValidator.driveLine(data.driveLine),
        engine: data.engine,
        engineSize: data.engineSize,
        exteriorColor: data.exteriorColor || data.exteriorColorFull,
        exteriorColorFull: data.exteriorColorFull,
        fuel: DealExchangeValidator.fuel(data.fuel)[0],
        highwayFuelEconomy: data.highwayFuelEconomy,
        imageUrl: data.imageUrl,
        initialRetailPrice: data.listedPrice,
        inServiceDate: data.inServiceDate,
        interiorColor: data.interiorColor || data.interiorColorFull,
        interiorColorFull: data.interiorColorFull,
        interiorMaterial: Array.isArray(data.interiorMaterial) ? data.interiorMaterial.join(', ') : data.interiorMaterial,
        inventoryDate: data.inventoryDate,
        invoiceAmount: data.invoicePrice,
        isCustom: !data.vin,
        listedPrice: data.listedPrice,
        location: data.location,
        make: data.make,
        mileage: data.odometer,
        model: data.model,
        notes: data.sellerComments,
        // options is an empty array because in acceleron it's also mapped as an empty array this should change if we change it in acceleron
        options: [],
        retailPrice: data.retailPrice,
        stockNumber: data.stockNumber,
        chromeStyleId: vehicleUtils.parseChromeStyleId(data.chromeId),
        transmission: data.transmissionFull?.primaryDescription || data.transmission,
        trim: data.trim,
        type: condition,
        // vehicleId: we overwrite this field because it's a uuid from previous vehicle
        vehicleId: '',
        vin: data.vin,
        year: data.year,
        wholesaleBookSource: DealExchangeValidator.wholesaleBookSource(data.bookoutSource),
        wholesaleValue: data.bookoutValue,
        bookouts: DealExchangeValidator.bookouts(data.bookouts),
        grossVehicleWeightRating: extractGvwr(data.grossVehicleWeightRating)
    };
};
